import {Inject, Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {of} from 'rxjs';
import {catchError, map, switchMap} from 'rxjs/operators';
import {TranscodingPreset, TranscodingPresetsService} from '../../services/api/transcoding-presets';
import * as TranscodingPresetsActions from './transcoding-presets.actions';

/**
 * Effects for handling a transcoding preset side effects
 * Manages all asynchronous operations like API calls
 */
@Injectable()
export class TranscodingPresetsEffects {
  // noinspection JSUnusedGlobalSymbols
  /**
   * Effect: Load all transcoding presets
   * Triggers on: loadTranscodingPresets
   * Success: loadTranscodingPresetsSuccess
   * Failure: loadTranscodingPresetsFailure
   */
  loadTranscodingPresets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TranscodingPresetsActions.loadTranscodingPresets),
      switchMap(() =>
        this.transcodingPresetsService.getAll().pipe(
          map((presets: TranscodingPreset[]) =>
            TranscodingPresetsActions.loadTranscodingPresetsSuccess({presets})
          ),
          catchError((error) =>
            of(
              TranscodingPresetsActions.loadTranscodingPresetsFailure({
                error: this.extractErrorMessage(error)
              })
            )
          )
        )
      )
    )
  );

  // noinspection JSUnusedGlobalSymbols
  /**
   * Effect: Load a single transcoding preset by ID
   * Triggers on: loadTranscodingPresetById
   * Success: loadTranscodingPresetByIdSuccess
   * Failure: loadTranscodingPresetByIdFailure
   */
  loadTranscodingPresetById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TranscodingPresetsActions.loadTranscodingPresetById),
      switchMap(({id}) =>
        this.transcodingPresetsService.getById(id).pipe(
          map((preset: TranscodingPreset) =>
            TranscodingPresetsActions.loadTranscodingPresetByIdSuccess({preset})
          ),
          catchError((error) =>
            of(
              TranscodingPresetsActions.loadTranscodingPresetByIdFailure({
                id,
                error: this.extractErrorMessage(error)
              })
            )
          )
        )
      )
    )
  );

  // noinspection JSUnusedGlobalSymbols
  /**
   * Effect: Create a new transcoding preset
   * Triggers on: createTranscodingPreset
   * Success: createTranscodingPresetSuccess
   * Failure: createTranscodingPresetFailure
   */
  createTranscodingPreset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TranscodingPresetsActions.createTranscodingPreset),
      switchMap(({dto}) =>
        this.transcodingPresetsService.create(dto).pipe(
          map((preset: TranscodingPreset) =>
            TranscodingPresetsActions.createTranscodingPresetSuccess({preset})
          ),
          catchError((error) =>
            of(
              TranscodingPresetsActions.createTranscodingPresetFailure({
                error: this.extractErrorMessage(error)
              })
            )
          )
        )
      )
    )
  );

  // noinspection JSUnusedGlobalSymbols
  /**
   * Effect: Delete a transcoding preset
   * Triggers on: deleteTranscodingPreset
   * Success: deleteTranscodingPresetSuccess
   * Failure: deleteTranscodingPresetFailure
   */
  deleteTranscodingPreset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TranscodingPresetsActions.deleteTranscodingPreset),
      switchMap(({id}) =>
        this.transcodingPresetsService.delete(id).pipe(
          map(() =>
            TranscodingPresetsActions.deleteTranscodingPresetSuccess({id})
          ),
          catchError((error) =>
            of(
              TranscodingPresetsActions.deleteTranscodingPresetFailure({
                id,
                error: this.extractErrorMessage(error)
              })
            )
          )
        )
      )
    )
  );

  constructor(
    private readonly actions$: Actions,
    @Inject(TranscodingPresetsService) private readonly transcodingPresetsService: TranscodingPresetsService
  ) {
  }

  /**
   * Helper method to extract an error message from an error object
   * Provides consistent error messaging across the application
   *
   * @param error - Error object from HTTP request
   * @returns Human-readable error message
   */
  private extractErrorMessage(error: any): string {
    if (typeof error === 'string') {
      return error;
    }

    if (error?.error?.message) {
      return error.error.message;
    }

    if (error?.message) {
      return error.message;
    }

    if (error?.statusText) {
      return error.statusText;
    }

    return 'An unknown error occurred';
  }
}
